import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { TemplateState } from '../../../types/common/types';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { SubscriptionType } from '../../types/SubscriptionType';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { LicenseTrialSubFormWebComponent } from '../../licenseTrialSubForm';
import {
  WEB_COMPONENT_LICENSE_BASIC_MONTHLY_SUBSCRIPTION_SUBFORM,
  WEB_COMPONENT_LICENSE_FRAT_SUBFORM,
  WEB_COMPONENT_LICENSE_MONTHLY_LICENSE_TRIAL_SUBFORM,
  WEB_COMPONENT_LICENSE_MONTHLY_SUBSCRIPTION_SUBFORM,
  WEB_COMPONENT_LICENSE_MONTHLY_SUBSCRIPTION_TRIAL_SUBFORM,
  WEB_COMPONENT_LICENSE_PRE_PAID_SUBFORM,
  WEB_COMPONENT_LICENSE_TRIAL_SUBFORM,
} from '../../../webcomponents.constants';
import { SelectOption } from '../../../ui/gzDropdown/types';
import { LicenseMainFormViewData } from '../types/LicenseMainFormViewData';
import { LicenseFormHelper } from '../controller/LicenseFormHelper';
import { LicenseFratSubFormWebComponent } from '../../licenseFratSubForm';
import { LicensePrePaidSubFormWebComponent } from '../../licensePrePaidSubForm';
import { LicenseBasicMonthlySubscriptionSubFormWebComponent } from '../../licenseBasicMonthlySubscriptionSubForm';
import { DropdownOptionsAvailability } from '../types/DropdownOptionsAvailability';
import { CompanyType } from '../../../company/types/company-type';
import { LicenseMonthlyLicenseTrialSubFormWebComponent } from '../../licenseMonthlyLicenseTrialSubForm';
import { LicenseMonthlySubscriptionTrialSubFormWebComponent } from '../../licenseMonthlySubscriptionTrialSubForm';
import { LicenseMonthlySubscriptionSubFormWebComponent } from '../../licenseMonthlySubscriptionSubForm';
import { CompanyData } from '../../../company/types/company-data';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';

export class LicenseMainFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  private dropdownOptionsAvailability: DropdownOptionsAvailability;
  private canManageNetworks: boolean;
  private canBeManagedFromAbove: boolean;
  private companyId?: string;
  private context: CompanyActionContext;

  public dom: {
    $wrapper: HTMLElement;

    $licensingTitle: GzTitleWebComponent;
    $licensingOptionsDropdown: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

    $subformSection: HTMLElement;

    subformComponents: {
      $trialSubForm: LicenseTrialSubFormWebComponent;
      $fratSubForm: LicenseFratSubFormWebComponent;
      $prePaidSubForm: LicensePrePaidSubFormWebComponent;
      $basicMonthlySubscriptionSubForm: LicenseBasicMonthlySubscriptionSubFormWebComponent;
      $monthlyLicenseTrialSubForm: LicenseMonthlyLicenseTrialSubFormWebComponent;
      $monthlySubscriptionTrialSubForm: LicenseMonthlySubscriptionTrialSubFormWebComponent;
      $monthlySubscriptionSubForm: LicenseMonthlySubscriptionSubFormWebComponent;
    };
    $licenseTypeInfoMessage: HTMLElement;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#wrapper'),

      $licensingTitle: this.shadow.querySelector('#licensing-title'),
      $licensingOptionsDropdown: this.shadow.querySelector('#license-type-form-field'),

      $subformSection: this.shadow.querySelector('#subform-section'),

      subformComponents: {
        $trialSubForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_TRIAL_SUBFORM),
        $fratSubForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_FRAT_SUBFORM),
        $prePaidSubForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_PRE_PAID_SUBFORM),
        $monthlyLicenseTrialSubForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_MONTHLY_LICENSE_TRIAL_SUBFORM),
        $basicMonthlySubscriptionSubForm: this.shadow.querySelector(
          WEB_COMPONENT_LICENSE_BASIC_MONTHLY_SUBSCRIPTION_SUBFORM
        ),
        $monthlySubscriptionTrialSubForm: this.shadow.querySelector(
          WEB_COMPONENT_LICENSE_MONTHLY_SUBSCRIPTION_TRIAL_SUBFORM
        ),
        $monthlySubscriptionSubForm: this.shadow.querySelector(WEB_COMPONENT_LICENSE_MONTHLY_SUBSCRIPTION_SUBFORM),
      },

      $licenseTypeInfoMessage: this.shadow.querySelector('#license-type-info-message'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings() {
    this.dom.$licensingTitle.innerText = this.dependencies.translateFn('Licensing');

    this.dom.$licensingOptionsDropdown.label = this.dependencies.translateFn('Options');
    this.dom.$licensingOptionsDropdown.Input.placeholder = this.dependencies.translateFn('Select an option');

    this.dom.$licenseTypeInfoMessage.innerText = this.dependencies.translateFn(
      'If you want to add a new license key, please use the "Add Product" button'
    );
  }

  public setupLicenseSubFormComponents(companyType: CompanyType) {
    // set company type
    if (this.dropdownOptionsAvailability.isPrePaidLicenseAvailable) {
      this.dom.subformComponents.$prePaidSubForm.setCompanyType(companyType);
      this.dom.subformComponents.$prePaidSubForm.resetValidators();
    }

    // update defaultProductDelegate feature
    if (this.dropdownOptionsAvailability.isBasicMonthlySubscriptionAvailable) {
      this.dom.subformComponents.$basicMonthlySubscriptionSubForm.updateDefaultProductDelegateFeature(companyType);
    }
  }

  buildSuccessView({ viewData, company }: { viewData: LicenseMainFormViewData; company: CompanyData | null }) {
    this.context = viewData.context;

    const companyType = company ? CompanyData.getCompanyType(company) : undefined;
    const manageNetworks = company?.specifics.rights.manageNetworks;

    let canBeManagedByAbove = company?.specifics.canBeManagedByAbove;
    if (canBeManagedByAbove === undefined && this.context === CompanyActionContext.Create) {
      canBeManagedByAbove = true;
    }

    this.companyId = company?._id;

    this.setCanManageNetworks(!!manageNetworks);
    this.setCanBeManagedFromAbove(!!canBeManagedByAbove);

    this.setDropdownOptionsAvailability(viewData, companyType, manageNetworks);
    this.setupLicensingOptionsDropdown();

    this.createFormGroup();

    this.startSubformWebComponents(viewData)
      .then(() => {
        if (company) {
          this.setCompanyData(company);
        }
      })
      .catch(console.error);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public updateSubscriptionDropdownOptions(
    viewData: LicenseMainFormViewData,
    companyType: CompanyType,
    canManageNetworks: boolean
  ): void {
    this.setDropdownOptionsAvailability(viewData, companyType, canManageNetworks);
    this.setSubscriptionDropdownOptions();

    this.startSubformWebComponents(viewData).catch(console.error);
  }

  protected onDisconnect() {
    this.formGroup?.unsubscribe();
  }

  public setCanManageNetworks(canManageNetworks: boolean) {
    this.canManageNetworks = canManageNetworks;
  }

  public setCanBeManagedFromAbove(canBeManagedFromAbove: boolean) {
    this.canBeManagedFromAbove = canBeManagedFromAbove;
    this.dom.subformComponents.$monthlySubscriptionSubForm.setCanBeManagedByAbove(canBeManagedFromAbove);
    this.dom.subformComponents.$prePaidSubForm.setCanBeManagedByAbove(canBeManagedFromAbove);
  }

  public getCanManageNetworks(): boolean {
    return this.canManageNetworks;
  }

  private setDropdownOptionsAvailability(
    viewData: LicenseMainFormViewData,
    companyType?: CompanyType,
    canManageNetworks?: boolean
  ) {
    this.dropdownOptionsAvailability = {
      isTrialAvailable: LicenseFormHelper.isTrialAvailable(
        viewData.companyLicenseInformation,
        viewData.context,
        canManageNetworks
      ),
      isFratAvailable: LicenseFormHelper.isFratAvailable(
        viewData.companyLicenseInformation,
        viewData.context,
        canManageNetworks
      ),
      isPrePaidLicenseAvailable: LicenseFormHelper.isPrePaidLicenseAvailable(
        viewData.companyLicenseInformation,
        viewData.context
      ),
      isBasicMonthlySubscriptionAvailable: LicenseFormHelper.isBasicMonthlySubscriptionAvailable(
        viewData.companyLicenseInformation,
        viewData.context
      ),
      isMonthlyLicenseTrialAvailable: LicenseFormHelper.isMonthlyLicenseTrialAvailable(
        viewData.companyLicenseInformation,
        viewData.context,
        companyType,
        canManageNetworks
      ),
      isMonthlyLicenseSubscriptionTrialAvailable: LicenseFormHelper.isMonthlySubscriptionTrialAvailable(
        viewData.companyLicenseInformation,
        viewData.context
      ),
      isMonthlySubscriptionAvailable: LicenseFormHelper.isMonthlySubscriptionAvailable(
        viewData.companyLicenseInformation,
        viewData.context
      ),
    };
  }

  private startSubformWebComponents(viewData: LicenseMainFormViewData): Promise<void[]> {
    const startPromises: Promise<void>[] = [];

    if (this.dropdownOptionsAvailability.isTrialAvailable) {
      this.dom.subformComponents.$trialSubForm.injectDependencies(this.dependencies);
      startPromises.push(this.dom.subformComponents.$trialSubForm.start());
    }

    if (this.dropdownOptionsAvailability.isFratAvailable) {
      this.dom.subformComponents.$fratSubForm.injectDependencies(this.dependencies);
      startPromises.push(this.dom.subformComponents.$fratSubForm.start());
    }

    if (this.dropdownOptionsAvailability.isPrePaidLicenseAvailable) {
      this.dom.subformComponents.$prePaidSubForm.injectDependencies(this.dependencies);
      startPromises.push(
        this.dom.subformComponents.$prePaidSubForm.start().then(() => {
          this.dom.subformComponents.$prePaidSubForm.setViewData(viewData);

          if (this.companyId) {
            this.dom.subformComponents.$prePaidSubForm.setCompanyId(this.companyId);
          }

          // if Manage Networks is unchecked, then we cannot input a license
          this.dom.subformComponents.$prePaidSubForm.setVisibilityForInfoMessageForDisabledLicenseKeyField(
            this.canManageNetworks,
            this.canBeManagedFromAbove,
            viewData.context
          );
          this.dom.subformComponents.$prePaidSubForm?.setDisabledForLicenseKeyField(!this.canManageNetworks);
          // clear the previous value of the license key field
          if (!this.canManageNetworks) {
            this.dom.subformComponents.$prePaidSubForm.resetLicensePrePaidForm();
          }
        })
      );
    }

    if (this.dropdownOptionsAvailability.isBasicMonthlySubscriptionAvailable) {
      this.dom.subformComponents.$basicMonthlySubscriptionSubForm.injectDependencies(this.dependencies);
      startPromises.push(
        this.dom.subformComponents.$basicMonthlySubscriptionSubForm.start().then(() => {
          this.dom.subformComponents.$basicMonthlySubscriptionSubForm.setViewData(viewData);
        })
      );
    }

    if (this.dropdownOptionsAvailability.isMonthlyLicenseTrialAvailable) {
      this.dom.subformComponents.$monthlyLicenseTrialSubForm.injectDependencies(this.dependencies);
      startPromises.push(this.dom.subformComponents.$monthlyLicenseTrialSubForm.start());
    }

    if (this.dropdownOptionsAvailability.isMonthlyLicenseSubscriptionTrialAvailable) {
      this.dom.subformComponents.$monthlySubscriptionTrialSubForm.injectDependencies(this.dependencies);
      startPromises.push(
        this.dom.subformComponents.$monthlySubscriptionTrialSubForm.start().then(() => {
          this.dom.subformComponents.$monthlySubscriptionTrialSubForm.setViewData(viewData);
        })
      );
    }

    if (this.dropdownOptionsAvailability.isMonthlySubscriptionAvailable) {
      this.dom.subformComponents.$monthlySubscriptionSubForm.injectDependencies(this.dependencies);
      startPromises.push(
        this.dom.subformComponents.$monthlySubscriptionSubForm.start().then(() => {
          this.dom.subformComponents.$monthlySubscriptionSubForm.setViewData(viewData);
          this.dom.subformComponents.$monthlySubscriptionSubForm.setCanBeManagedByAbove(this.canBeManagedFromAbove);
        })
      );
    }

    return Promise.all(startPromises);
  }

  private setupLicensingOptionsDropdown(): void {
    this.setSubscriptionDropdownOptions();

    this.dom.$licensingOptionsDropdown.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select a licensing method')),
    ];
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      subscription: this.dom.$licensingOptionsDropdown,
    });
  }

  private setCompanyData(company: CompanyData): void {
    const subscriptionType = CompanyData.determineCompanySubscription(company);

    this.dom.$licensingOptionsDropdown.value = subscriptionType;

    switch (subscriptionType) {
      case SubscriptionType.Inherited:
      case SubscriptionType.BasicMonthly:
        if (this.dropdownOptionsAvailability.isMonthlySubscriptionAvailable) {
          this.dom.subformComponents.$monthlySubscriptionSubForm.setCompanyData(company);
        }
        if (this.dropdownOptionsAvailability.isBasicMonthlySubscriptionAvailable) {
          this.dom.subformComponents.$basicMonthlySubscriptionSubForm.setCompanyData(company);
        }
        break;
      case SubscriptionType.InheritedMspTrial:
        this.dom.subformComponents.$monthlySubscriptionTrialSubForm.setCompanyData(company);
        break;
      case SubscriptionType.Trial:
        this.dom.subformComponents.$trialSubForm.setCompanyData(company);
        break;
    }
  }

  private getLabelForPrePaidDropdownOption() {
    return this.context === CompanyActionContext.Edit
      ? this.dependencies.translateFn('Existing License Key')
      : this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.Licensed);
  }

  private setSubscriptionDropdownOptions() {
    const options: Array<SelectOption> = [];

    if (this.dropdownOptionsAvailability.isTrialAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.Trial),
        value: SubscriptionType.Trial,
      });
    }

    if (this.dropdownOptionsAvailability.isFratAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.Frat),
        value: SubscriptionType.Frat,
      });
    }

    if (this.dropdownOptionsAvailability.isPrePaidLicenseAvailable) {
      options.push({
        title: this.getLabelForPrePaidDropdownOption(),
        value: SubscriptionType.Licensed,
      });
    }

    if (this.dropdownOptionsAvailability.isBasicMonthlySubscriptionAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.BasicMonthly),
        value: SubscriptionType.BasicMonthly,
      });
    }

    if (this.dropdownOptionsAvailability.isMonthlyLicenseTrialAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.MonthlyLicenseTrial),
        value: SubscriptionType.MonthlyLicenseTrial,
      });
    }

    if (this.dropdownOptionsAvailability.isMonthlyLicenseSubscriptionTrialAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.InheritedMspTrial),
        value: SubscriptionType.InheritedMspTrial,
      });
    }

    if (this.dropdownOptionsAvailability.isMonthlySubscriptionAvailable) {
      options.push({
        title: this.stringsTranslator.getTranslatedSubscriptionType(SubscriptionType.Inherited),
        value: SubscriptionType.Inherited,
      });
    }

    this.dom.$licensingOptionsDropdown.Input.options = options;
  }
}
